import { useEffect, useState } from 'react'
import { getPrintMailConfig } from 'services'

import { IPrintMailConfig, IPrintMailStatus } from './types'

export interface IPrintMailOffer {
  config: IPrintMailConfig | null
  status: IPrintMailStatus | null
  /** Purposes the tenant has actually switched on, which are the only ones to offer. */
  purposes: string[]
}

const empty: IPrintMailOffer = { config: null, status: null, purposes: [] }

/**
 * One read of `GET /print-mail/config` per page load, shared by every caller.
 *
 * The "Mail this" action hangs off the loan documents panel, which can mount on any loan screen,
 * and the answer is a company-level setting that does not change while somebody is looking at a
 * loan. A promise rather than a value, so several components mounting in the same tick join one
 * request instead of each making their own.
 */
let pending: Promise<IPrintMailOffer> | null = null

const read = (): Promise<IPrintMailOffer> => {
  if (pending) return pending
  pending = getPrintMailConfig()
    .then((res: any) => {
      const config: IPrintMailConfig | null = res?.config || null
      return {
        config,
        status: res?.status || null,
        purposes: Object.entries(config?.purposes || {})
          .filter(([, value]: [string, any]) => value?.enabled === true)
          .map(([purpose]) => purpose)
          .sort(),
      }
    })
    .catch(() => {
      // A user without the PrintMail permission gets a 403 here, which is the normal answer for
      // them and not something to put on their screen. Nothing is offered and nothing is said.
      pending = null
      return empty
    })
  return pending
}

/**
 * Whether this company is set up to post physical mail, and under which purposes.
 *
 * `enabled` only when the service is on AND at least one purpose is switched on: a tenant who has
 * turned mailing on but configured no communication types has not decided to post anything yet,
 * and offering the action would be offering something that cannot be done.
 */
export const usePrintMailOffer = (active: boolean) => {
  const [offer, setOffer] = useState<IPrintMailOffer>(empty)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    read().then((value) => {
      if (!cancelled) setOffer(value)
    })
    return () => {
      cancelled = true
    }
  }, [active])

  return { ...offer, canOffer: !!offer.config?.enabled && offer.purposes.length > 0 }
}
